import { PomodoroService } from "features/widget-workspace/service/PomodoroService";
import React, { useEffect, useReducer } from "react";
import { useParams } from "react-router-dom";
import { TimerAlarmSoundsMap } from "@lifeat/shared/data-models/widget/Pomodoro/PomodoroWidgetSettingsEntity";
import { useUserContext } from "@contexts/UserContext";
import { logError } from "@service/ConsoleLoggerService";
import { updateFocusTime } from "@user/service/AllUserMetricsService";
import { RoomParams } from "../../../../../room/RoomTypes";
import { getAlarmSound, getTimerDisplayText } from "../PomodoroUtil";
import { LIFEAT_SPACES_DOC_TITLE, SHORT_BREAK_TARGET, TIMER_DOC_TITLE_SUFFIX } from "../TimerWidgetConstants";
import { useCountdownTimer } from "./useCountdownTimer";
import { usePomodoroSettingsInput } from "./usePomodoroSettingsInput";
import { useSelectedPomodoroSound } from "./useSelectedPomodoroSound";

type HeadfulPomodoroState = {
    pomodoroCount: number;
    shortBreakCount: number;
    /** Flag indicating whether the timer is transitioning to the next. */
    awaitingTransition: boolean;
};
type HeadfulPomodoroAction =
    | { type: "incrementPomodoroCount" }
    | { type: "incrementShortBreakCount" }
    | { type: "resetShortBreakCount" }
    | { type: "startTransition" }
    | { type: "endTransition" };

function headfulPomodoroReducer(state: HeadfulPomodoroState, action: HeadfulPomodoroAction): HeadfulPomodoroState {
    switch (action.type) {
        case "incrementPomodoroCount":
            return {
                ...state,
                pomodoroCount: state.pomodoroCount + 1,
            };
        case "incrementShortBreakCount":
            return {
                ...state,
                shortBreakCount: state.shortBreakCount + 1,
            };
        case "resetShortBreakCount":
            return {
                ...state,
                shortBreakCount: 0,
            };
        case "startTransition":
            return {
                ...state,
                awaitingTransition: true,
            };
        case "endTransition":
            return {
                ...state,
                awaitingTransition: false,
            };
    }
}

type Options = {
    hidden: boolean;
    pomodoroService: PomodoroService;
    alarmSoundOptions: TimerAlarmSoundsMap;
    onPomodoroCountChange: (count: number) => void;
    onHidePomodoroCountChange: (value: boolean) => void;
    audioRef: React.MutableRefObject<HTMLAudioElement | null>;
};
/**
 * A headful pomodoro timer is a "smart" version of the timer which
 * can be used standalone.
 */
export function useLocalPomodoro(options: Options) {
    const { roomId } = useParams<RoomParams>();
    const { user, userData } = useUserContext();

    const { pomodoroSettingsInput, dispatchPomodoroSettingsInput, duration, saveSettings } = usePomodoroSettingsInput(
        options.pomodoroService,
        roomId,
    );
    const {
        selectedAlarmSound,
        isAutoStartTimer,
        pomodoroTimerDuration,
        shortBreakTimerDuration,
        longBreakTimerDuration,
        timerType,
        hidePomodoroCount,
    } = pomodoroSettingsInput;

    useSelectedPomodoroSound(options.pomodoroService, pomodoroSettingsInput, dispatchPomodoroSettingsInput);

    const [state, dispatch] = useReducer(headfulPomodoroReducer, {
        pomodoroCount: 0,
        shortBreakCount: 0,
        awaitingTransition: false,
    });
    const { pomodoroCount, shortBreakCount } = state;

    useEffect(() => {
        options.onPomodoroCountChange(pomodoroCount);
    }, [pomodoroCount]);

    useEffect(() => {
        options.onHidePomodoroCountChange(hidePomodoroCount);
    }, [hidePomodoroCount]);

    const start = (fromClick = false) => {
        if (options.audioRef.current == null) {
            logError(`audioRef is null, cannot load()`);
        }
        // Note: load audio on user interaction to handle blocked autoplay on safari mobile
        options.audioRef.current?.load();
        countdownTimer.start();

        options.pomodoroService.logTimerStartEvent?.(timerType, fromClick);
    };

    const pause = () => {
        countdownTimer.pause();
        options.pomodoroService.stopServerTimer?.(roomId).then(() => countdownTimer.reset(false));
    };

    const reset = () => {
        countdownTimer.reset(false);
    };

    const handleTimerEnd = () => {
        // if user is authenticated and the completed timer is pomodoro, update their focus time metric
        if (user && userData && timerType === "pomodoro") {
            updateFocusTime(duration("pomodoro"));
        }

        options.pomodoroService.logTimerEndEvent?.(timerType);

        if (isAutoStartTimer) {
            // Just got done with the study session,
            if (timerType === "pomodoro") {
                // ...so increment the pomodoro count...
                dispatch({ type: "incrementPomodoroCount" });

                // ...and determine what type of break to take next.
                if (shortBreakCount >= SHORT_BREAK_TARGET && shortBreakCount !== 0) {
                    dispatchPomodoroSettingsInput({ type: "setTimerType", value: "long_break" });
                    dispatch({ type: "resetShortBreakCount" });
                } else {
                    dispatchPomodoroSettingsInput({ type: "setTimerType", value: "short_break" });
                }
            } else {
                // Just got done with a break, so increment the count of the appropriate type.
                if (timerType === "short_break") {
                    dispatch({ type: "incrementShortBreakCount" });
                }

                // ...and get ready (and possibly auto-start) the next study session,
                dispatchPomodoroSettingsInput({ type: "setTimerType", value: "pomodoro" });
            }

            dispatch({ type: "startTransition" });
        } else {
            if (timerType === "pomodoro") {
                dispatch({ type: "incrementPomodoroCount" });
            }

            countdownTimer.reset(false);
        }
    };

    const selectPomodoro = () => {
        dispatchPomodoroSettingsInput({ type: "setTimerType", value: "pomodoro" });
    };

    const selectShortBreak = () => {
        dispatchPomodoroSettingsInput({ type: "setTimerType", value: "short_break" });
    };

    const selectLongBreak = () => {
        dispatchPomodoroSettingsInput({ type: "setTimerType", value: "long_break" });
    };

    // Pause & reset timer when configuration changes.
    useEffect(() => {
        // Only when not auto-transitioning.
        if (!state.awaitingTransition) {
            countdownTimer.reset(false);
        }
    }, [timerType, pomodoroTimerDuration, shortBreakTimerDuration, longBreakTimerDuration]);

    // Handle timer auto-transition.
    useEffect(() => {
        if (state.awaitingTransition) {
            countdownTimer.reset(true);
            dispatch({ type: "endTransition" });
        }
    }, [state.awaitingTransition]);

    const handleAlarm = () => {
        if (options.audioRef.current == null) {
            logError(`Audio Ref in useHeadfulPomodoro is null!`);
        } else {
            options.audioRef.current?.play().catch((e) => logError("Error playing pomodoro alarm audio: " + e));
        }
    };

    const countdownTimer = useCountdownTimer({
        duration: duration(timerType) * 60 * 1000,
        onTimerEnd: handleTimerEnd,
        onAlarm: handleAlarm,
        alarmRingOffset: getAlarmSound(options.alarmSoundOptions, selectedAlarmSound).soundDuration,
    });

    const timerDisplayText = getTimerDisplayText(countdownTimer.state.currMillis);
    const isRunning = !!countdownTimer.state.timer;

    useEffect(() => {
        if (!options.hidden) {
            if (isRunning) {
                document.title = `${timerDisplayText}${TIMER_DOC_TITLE_SUFFIX}`;
            } else {
                document.title = LIFEAT_SPACES_DOC_TITLE;
            }
        }
    }, [isRunning, timerDisplayText, options.hidden]);

    return {
        selectPomodoro,
        selectShortBreak,
        selectLongBreak,
        saveSettings,
        start,
        pause,
        reset,
        isRunning,
        timerDisplayText,
        pomodoroSettingsInput,
        dispatchPomodoroSettingsInput,
    };
}
